import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import useReveal from "./useReveal";
import useStatusStore from "../../store/statusStore";
import { SITE_NAME } from "../../config/location";

const TITLES = {
    "/karte": "Speisekarte",
    "/bestellen": "Bestellen",
    "/checkout": "Kasse",
    "/reservierung": "Tisch reservieren",
    "/ueber-uns": "Über uns",
    "/galerie": "Galerie",
    "/feiern": "Feiern & Catering",
    "/kontakt": "Kontakt & Anfahrt",
    "/impressum": "Impressum",
    "/datenschutz": "Datenschutz",
};

// Rahmen der öffentlichen Seiten: Header, Footer, Reveal-Animationen, Scroll nach oben.
export default function SiteLayout({ children }) {
    const { pathname, search, hash, key } = useLocation();
    const lastLocation = useRef(null);
    const mainRef = useRef(null);
    const fetchClosure = useStatusStore((state) => state.fetchClosure);
    useReveal(mainRef, pathname);

    useEffect(() => {
        fetchClosure();
    }, [fetchClosure]);

    useEffect(() => {
        const page = TITLES[pathname];
        document.title = page
            ? `${page} – ${SITE_NAME}`
            : `${SITE_NAME} – Griechische Küche in Weingarten (Baden)`;
    }, [pathname]);

    // Beim Seitenwechsel sofort springen (vor dem Zeichnen, ohne Animation). Sanftes Scrollen
    // bricht Safari auf dem iPhone ab, sobald die neue Seite nachlädt und ihre Höhe ändert;
    // die Seite bleibt dann irgendwo stehen (z. B. Weinkarte bei den Rotweinen).
    // Gilt für jede Navigation (key), auch beim erneuten Klick auf die aktuelle Seite (z. B. Logo).
    // Ausnahme: Nur die Suche ändert sich auf derselben Seite (Reiter der Speisekarte).
    useLayoutEffect(() => {
        const last = lastLocation.current;
        lastLocation.current = { pathname, search, hash };
        const onlySearchChanged = last && last.pathname === pathname && last.search !== search && !hash;
        if (onlySearchChanged) return;

        const target = hash && document.getElementById(hash.slice(1));
        if (target) {
            target.scrollIntoView({ behavior: "instant" });
        } else {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        }
    }, [pathname, search, hash, key]);

    return (
        <div className="site flex min-h-screen flex-col">
            <a
                href="#inhalt"
                className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-forest focus:px-4 focus:py-3 focus:!text-creme"
            >
                Zum Inhalt springen
            </a>
            <SiteHeader />
            <main id="inhalt" ref={mainRef} className="flex-grow">
                {children}
            </main>
            <SiteFooter />
        </div>
    );
}
